/**
 * DailyRewardClaimDialog — single-action reward modal (story 01-03)
 *
 * Matches Figma frame 5738-107409:
 *   - Gold Dialog variant, title "RÉCOMPENSE" (claimModal.title)
 *   - Top-right ✕ close via Dialog's built-in showCloseButton prop
 *   - Body: "VOUS AVEZ GAGNÉ" (claimModal.message) + large gold amount + coin icon
 *   - Single green RÉCUPÉRER button (claimModal.confirm)
 *   - No Cancel button — dismissal via ✕ or click-outside (when not claiming)
 *
 * AC4 path taken: Dialog already exposes `showCloseButton` prop (renders a ✕ Button
 * at top-right via the `Button` component). Passing `showCloseButton={true}` is
 * sufficient — no modification to shared Dialog required.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { PillButton } from '@/shared/components/PillButton';
import { coinImg as coinIcon } from '@/assets';

const AMOUNT_STYLE = {
  textShadow:
    '3px 3px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 2px 2px 6px rgba(0,0,0,0.5)'
} as const;

export interface DailyRewardClaimDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Bonus amount to be credited on confirm */
  amount: number;
  /** True while a claim mutation is in-flight */
  isClaiming: boolean;
}

export const DailyRewardClaimDialog = memo(function DailyRewardClaimDialog({
  isOpen,
  onClose,
  onConfirm,
  amount,
  isClaiming
}: DailyRewardClaimDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      variant="gold"
      title={t('dailyRewards.claimModal.title')}
      showCloseButton={true}
      allowClickOutsideToClose={!isClaiming}
    >
      <div className="space-y-4">
        {/* Reward body: message + big gold amount + coin icon */}
        <div className="flex flex-col items-center gap-2">
          <span className="font-mind-boggle text-lg text-[#333] leading-tight text-center">
            {t('dailyRewards.claimModal.message')}
          </span>
          <div className="flex items-center gap-2">
            <span
              className="font-mind-boggle text-4xl text-[#FCD34D] leading-tight"
              style={AMOUNT_STYLE}
            >
              {amount}
            </span>
            <img src={coinIcon} alt="" className="h-8 w-8 object-contain" />
          </div>
        </div>

        {/* Single confirm action — no Cancel button */}
        <PillButton
          type="button"
          variant="green"
          data-testid="daily-reward-claim-confirm-btn"
          onClick={onConfirm}
          disabled={isClaiming}
          className="w-full h-12 [&_span]:!text-base"
        >
          {isClaiming ? t('common.loading') : t('dailyRewards.claimModal.confirm')}
        </PillButton>
      </div>
    </Dialog>
  );
});

export default DailyRewardClaimDialog;
